<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { AsYouType } from 'libphonenumber-js';
import axios from 'axios';
import ConversationAPI from 'dashboard/api/inbox/conversation';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactCampaign from './contact/ContactCampaign.vue';
import GroupMembers from './contact/GroupMembers.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const { updateUISettings, isContactSidebarItemOpen } = useUISettings();

const contactInfoRef = ref(null);
const groupMembersRef = ref(null);
const liveWhatsappGroup = ref(null);
const showClearConversationModal = ref(false);
const isClearingConversation = ref(false);
const showBlockConversationModal = ref(false);
const isUpdatingConversationBlock = ref(false);
const showLeaveGroupModal = ref(false);
const isLeavingWhatsappGroup = ref(false);
const showDeleteConversationModal = ref(false);
const isDeletingConversation = ref(false);
const { t } = useI18n();
const route = useRoute();

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled } = useAccount();

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');
const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](
    props.inboxId || currentChat.value?.inbox_id
  )
);
const isPersonalConversation = computed(() => {
  const labels = Array.isArray(currentChat.value?.labels)
    ? currentChat.value.labels
    : [];
  const attrs = currentChat.value?.additional_attributes || {};
  const inboxAttrs = currentInbox.value?.additional_attributes || {};

  return (
    labels.includes('pessoal') ||
    isAceleresIndividualOwn(inboxAttrs.connection_type) ||
    Number(attrs.personal_owner_chatwoot_user_id) > 0 ||
    Number(attrs.personal_origin_chatwoot_user_id) > 0 ||
    Number(attrs.owner_chatwoot_user_id) > 0
  );
});
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);
const conversationCampaign = computed(() => currentChat.value.campaign || {});
const conversationCustomAttributes = computed(
  () => currentChat.value.custom_attributes || {}
);
const groupClearConversationLabel = 'Limpar conversa';
const groupLeaveLabel = 'Sair do grupo';
const clearConversationLabel = 'Limpar conversa';
const clearConversationTitle = 'Deseja limpar esta conversa?';
const clearConversationDescription =
  'As mensagens serão removidas de todos os dispositivos.';
const confirmClearConversationLabel = 'Limpar';
const clearingConversationLabel = 'Limpando...';
const leaveGroupTitle = 'Deseja sair deste grupo?';
const leaveGroupDescription =
  'Você deixará o grupo no WhatsApp e deixará de receber novas mensagens.';
const confirmLeaveGroupLabel = 'Sair do grupo';
const leavingGroupLabel = 'Saindo...';
const deleteConversationLabel = 'Apagar conversa';
const deleteConversationDescription =
  'As mensagens serão removidas de todos os dispositivos.';
const cancelDeleteConversationLabel = 'Cancelar';
const confirmDeleteConversationLabel = 'Apagar';
const deletingConversationLabel = 'Apagando...';
const groupCreatedByLabel = 'Grupo criado por';
const groupNumberUnavailableLabel = 'número não disponível';
const groupCreatedSeparator = '·';
const contactPanelAccordionTitles = Object.freeze({
  notes: 'Notas do contato',
  attributes: 'Atributos do contato',
  conversationInfo: 'Informações da conversa',
  conversationActions: 'Ações da conversa',
  campaigns: 'Campanhas',
});
const CONTACT_SIDEBAR_ACCORDION_KEYS = Object.freeze([
  'is_conv_actions_open',
  'is_conv_details_open',
  'is_contact_attributes_open',
  'is_previous_conv_open',
  'is_macro_open',
  'is_linear_issues_open',
  'is_shopify_orders_open',
  'is_contact_notes_open',
  'is_campaign_info_open',
]);

const getClosedContactSidebarItems = () =>
  CONTACT_SIDEBAR_ACCORDION_KEYS.reduce((settings, key) => {
    settings[key] = false;
    return settings;
  }, {});

const resetContactSidebarItems = () => {
  updateUISettings(getClosedContactSidebarItems());
};

const toggleSidebarUIState = key => {
  const settings = getClosedContactSidebarItems();

  if (!isContactSidebarItemOpen(key)) {
    settings[key] = true;
  }

  updateUISettings(settings);
};

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value?.additional_attributes || {}
);
const groupAttributes = computed(() => ({
  ...(currentChat.value?.meta?.sender?.additional_attributes || {}),
  ...(currentChat.value?.meta?.sender?.custom_attributes || {}),
  ...(contact.value?.additional_attributes || {}),
  ...(contact.value?.custom_attributes || {}),
}));
const whatsappGroup = computed(() => {
  const storedGroup = groupAttributes.value.whatsapp_group || {};
  const liveGroup = liveWhatsappGroup.value;

  return liveGroup && liveGroup.jid === storedGroup.jid
    ? { ...storedGroup, ...liveGroup }
    : storedGroup;
});
const isWhatsappGroup = computed(
  () =>
    groupAttributes.value.is_whatsapp_group === true ||
    groupAttributes.value.is_whatsapp_group === 'true'
);
const contactPanelTitle = computed(() =>
  isWhatsappGroup.value ? 'Dados do grupo' : 'Dados do contato'
);
const deleteConversationContactLabel = computed(() => {
  const sender = currentChat.value?.meta?.sender || {};
  return sender.phone_number || sender.name || sender.identifier || '';
});
const deleteConversationTitle = computed(() => {
  const contactLabel = deleteConversationContactLabel.value;
  return contactLabel
    ? `Deseja apagar a conversa com ${contactLabel}?`
    : 'Deseja apagar a conversa?';
});
const hasLeftWhatsappGroup = ref(false);
const groupCreator = computed(() => {
  const group = whatsappGroup.value;
  const creator =
    group.created_by ||
    group.createdBy ||
    group.creator ||
    group.owner ||
    group.owner_phone ||
    group.owner_phone_number ||
    group.participants?.find(
      participant =>
        participant?.is_owner ||
        participant?.is_creator ||
        participant?.role === 'owner' ||
        participant?.role === 'creator'
    );

  if (!creator) return { name: '', phone: '' };
  if (typeof creator === 'string') return { name: '', phone: creator };

  return {
    name:
      creator.name ||
      creator.push_name ||
      creator.pushName ||
      creator.notify_name ||
      '',
    phone:
      creator.phone_number ||
      creator.phoneNumber ||
      creator.number ||
      creator.id ||
      '',
  };
});
const groupCreatedByName = computed(() => groupCreator.value.name);
const groupCreatedByPhone = computed(() => {
  const rawPhone = String(groupCreator.value.phone || '').replace(/@.*/, '');
  if (!rawPhone) return '';

  const digits = rawPhone.replace(/\D/g, '');
  if (!digits) return rawPhone;

  const formatted = digits.startsWith('55')
    ? new AsYouType('BR').input(`+${digits}`)
    : new AsYouType().input(`+${digits}`);

  return formatted || `+${digits}`;
});
const groupCreatedAt = computed(
  () =>
    whatsappGroup.value.created_at ||
    whatsappGroup.value.createdAt ||
    whatsappGroup.value.creation_date ||
    whatsappGroup.value.creation_time ||
    groupAttributes.value.whatsapp_group_created_at ||
    currentChat.value?.created_at ||
    ''
);
const groupCreatedAtLabel = computed(() => {
  if (!groupCreatedAt.value) return 'Data não disponível';
  const date = new Date(groupCreatedAt.value);
  if (Number.isNaN(date.getTime())) return String(groupCreatedAt.value);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
});
const isConversationMuted = computed(() => currentChat.value?.muted);
const blockContactLabel = computed(() =>
  isConversationMuted.value ? 'Desbloquear' : 'Bloquear'
);
const blockContactIdentity = computed(() => {
  const name =
    contact.value?.name?.trim() ||
    currentChat.value?.meta?.sender?.name?.trim();
  if (name) return name;

  return (
    contact.value?.phone_number ||
    contact.value?.phoneNumber ||
    contact.value?.identifier ||
    currentChat.value?.meta?.sender?.phone_number ||
    currentChat.value?.meta?.sender?.phoneNumber ||
    currentChat.value?.meta?.sender?.identifier ||
    'este contato'
  );
});
const blockConversationTitle = computed(() =>
  isConversationMuted.value
    ? `Deseja desbloquear ${blockContactIdentity.value}?`
    : `Deseja bloquear ${blockContactIdentity.value}?`
);
const blockConversationDescription = computed(() =>
  isConversationMuted.value
    ? 'O contato poderá voltar a enviar mensagens nesta conversa.'
    : 'Você não receberá novas mensagens deste contato nesta conversa.'
);
const confirmBlockConversationLabel = computed(() => blockContactLabel.value);
const updatingBlockConversationLabel = 'Atualizando...';

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

const refreshWhatsappGroup = group => {
  if (group?.jid) {
    liveWhatsappGroup.value = group;
  }
  getContactDetails();
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

watch(conversationId, () => {
  hasLeftWhatsappGroup.value = false;
  liveWhatsappGroup.value = null;
  resetContactSidebarItems();
});

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

const openContactEditor = () => {
  contactInfoRef.value?.toggleEditModal?.();
};

const scrollToGroupMembers = () => {
  const element = groupMembersRef.value?.$el || groupMembersRef.value;
  element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const toggleMuteConversation = async () => {
  if (!currentChat.value?.id) return;

  isUpdatingConversationBlock.value = true;
  try {
    if (isConversationMuted.value) {
      await store.dispatch('unmuteConversation', currentChat.value.id);
      useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    } else {
      await store.dispatch('muteConversation', currentChat.value.id);
      useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
    }
    showBlockConversationModal.value = false;
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        'Não foi possível atualizar o bloqueio deste contato.'
    );
  } finally {
    isUpdatingConversationBlock.value = false;
  }
};

const openBlockConversationModal = () => {
  showBlockConversationModal.value = true;
};

const closeBlockConversationModal = () => {
  if (isUpdatingConversationBlock.value) return;
  showBlockConversationModal.value = false;
};

const clearConversation = async () => {
  if (!currentChat.value?.id) return;

  isClearingConversation.value = true;
  try {
    await ConversationAPI.clear(currentChat.value.id);
    useAlert('Conversa limpa com sucesso.');
    showClearConversationModal.value = false;
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Não foi possível limpar a conversa.'
    );
  } finally {
    isClearingConversation.value = false;
  }
};

const openClearConversationModal = () => {
  showClearConversationModal.value = true;
};

const closeClearConversationModal = () => {
  if (isClearingConversation.value) return;
  showClearConversationModal.value = false;
};

const deleteConversation = async () => {
  if (!currentChat.value?.id) return;

  isDeletingConversation.value = true;
  try {
    await store.dispatch('deleteConversation', currentChat.value.id);
    useAlert('Conversa apagada com sucesso.');
    showDeleteConversationModal.value = false;
    closeContactPanel();
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Não foi possível apagar a conversa.'
    );
  } finally {
    isDeletingConversation.value = false;
  }
};

const openDeleteConversationModal = () => {
  showDeleteConversationModal.value = true;
};

const closeDeleteConversationModal = () => {
  if (isDeletingConversation.value) return;
  showDeleteConversationModal.value = false;
};

const leaveWhatsappGroup = async () => {
  const groupJid = whatsappGroup.value?.jid;
  if (!props.inboxId || !groupJid) return;

  isLeavingWhatsappGroup.value = true;
  try {
    await axios.delete(
      `https://homolog.aceleres.ai/api/wame/groups/${props.inboxId}`,
      {
        headers: {
          'X-Chatwoot-Token': currentUser.value?.access_token || '',
          'X-Chatwoot-Account-Id': String(route.params.accountId || ''),
        },
        data: { group_jid: groupJid },
      }
    );
    hasLeftWhatsappGroup.value = true;
    showLeaveGroupModal.value = false;
    useAlert('Você saiu do grupo no WhatsApp.');
  } catch (error) {
    useAlert(
      error.response?.data?.message || 'Não foi possível sair do grupo.'
    );
  } finally {
    isLeavingWhatsappGroup.value = false;
  }
};

const openLeaveGroupModal = () => {
  showLeaveGroupModal.value = true;
};

const closeLeaveGroupModal = () => {
  if (isLeavingWhatsappGroup.value) return;
  showLeaveGroupModal.value = false;
};

onMounted(() => {
  resetContactSidebarItems();
  getContactDetails();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
});
</script>

<template>
  <div
    class="contact-details-panel flex h-full min-h-0 w-full min-w-0 max-w-full flex-col overflow-hidden"
  >
    <header
      class="contact-details-panel__header flex !h-14 flex-shrink-0 items-center justify-between gap-3 bg-white !px-3 md:!h-16 md:!gap-4 md:!px-5"
    >
      <div class="flex min-w-0 items-center gap-3">
        <Button
          v-tooltip.top="$t('GENERAL.CLOSE')"
          icon="i-lucide-x"
          ghost
          class="contact-details-panel__header-button contact-details-panel__header-button--close text-n-slate-12"
          @click="closeContactPanel"
        />
        <h2 class="m-0 truncate text-base font-semibold text-n-slate-12">
          {{ contactPanelTitle }}
        </h2>
      </div>
      <Button
        v-if="contact?.id && !isWhatsappGroup"
        v-tooltip.top="$t('EDIT_CONTACT.BUTTON_LABEL')"
        icon="i-lucide-pencil"
        ghost
        class="contact-details-panel__header-button contact-details-panel__header-button--edit text-n-slate-12"
        @click="openContactEditor"
      />
    </header>
    <div
      class="contact-details-panel__body min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain"
    >
      <ContactInfo
        ref="contactInfoRef"
        :contact="contact"
        :channel-type="channelType"
        :inbox-id="inboxId"
        @group-members-navigate="scrollToGroupMembers"
        @group-updated="refreshWhatsappGroup"
      />
      <div class="px-4 pb-8 list-group">
        <div
          class="contact-details-panel__accordion-list flex flex-col !gap-2 md:!gap-5"
        >
          <AccordionItem
            class="contact-details-panel__accordion contact-details-panel__accordion--notes"
            :title="contactPanelAccordionTitles.notes"
            :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_contact_notes_open')"
          >
            <template #icon>
              <span
                class="i-lucide-sticky-note contact-details-panel__accordion-icon"
              />
            </template>
            <ContactNotes :contact-id="contactId" />
          </AccordionItem>

          <AccordionItem
            class="contact-details-panel__accordion contact-details-panel__accordion--attributes"
            :title="contactPanelAccordionTitles.attributes"
            :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_contact_attributes_open')"
          >
            <template #icon>
              <span
                class="i-lucide-tags contact-details-panel__accordion-icon"
              />
            </template>
            <CustomAttributes
              attribute-type="contact_attribute"
              attribute-from="conversation_contact_panel"
              :contact-id="contact.id"
              :empty-state-message="
                $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
              "
            />
          </AccordionItem>

          <AccordionItem
            class="contact-details-panel__accordion contact-details-panel__accordion--conversation-info"
            :title="contactPanelAccordionTitles.conversationInfo"
            :is-open="isContactSidebarItemOpen('is_conv_details_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_conv_details_open')"
          >
            <template #icon>
              <span
                class="i-lucide-info contact-details-panel__accordion-icon"
              />
            </template>
            <ConversationInfo
              :conversation-attributes="conversationAdditionalAttributes"
              :contact-attributes="contactAdditionalAttributes"
            />
          </AccordionItem>

          <div class="conversation--actions">
            <AccordionItem
              class="contact-details-panel__accordion contact-details-panel__accordion--actions"
              :title="contactPanelAccordionTitles.conversationActions"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              show-chevron
              animated
              :draggable="false"
              @toggle="toggleSidebarUIState('is_conv_actions_open')"
            >
              <template #icon>
                <span
                  class="i-lucide-settings-2 contact-details-panel__accordion-icon"
                />
              </template>
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>

          <AccordionItem
            class="contact-details-panel__accordion contact-details-panel__accordion--campaign"
            :title="contactPanelAccordionTitles.campaigns"
            :is-open="isContactSidebarItemOpen('is_campaign_info_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_campaign_info_open')"
          >
            <template #icon>
              <span
                class="i-lucide-megaphone contact-details-panel__accordion-icon"
              />
            </template>
            <ContactCampaign
              :contact="contact"
              :conversation-campaign="conversationCampaign"
              :conversation-attributes="conversationAdditionalAttributes"
              :conversation-custom-attributes="conversationCustomAttributes"
            />
          </AccordionItem>

          <AccordionItem
            v-if="contact.id && !isPersonalConversation"
            class="contact-details-panel__accordion contact-details-panel__accordion--previous-conversations"
            :title="$t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')"
            :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_previous_conv_open')"
          >
            <template #icon>
              <span
                class="i-lucide-message-square-text contact-details-panel__accordion-icon"
              />
            </template>
            <ContactConversations
              :contact-id="contact.id"
              :conversation-id="conversationId"
            />
          </AccordionItem>

          <woot-feature-toggle feature-key="macros">
            <AccordionItem
              class="contact-details-panel__accordion contact-details-panel__accordion--macros"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              show-chevron
              animated
              :draggable="false"
              @toggle="toggleSidebarUIState('is_macro_open')"
            >
              <template #icon>
                <span
                  class="i-lucide-bolt contact-details-panel__accordion-icon"
                />
              </template>
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>

          <AccordionItem
            v-if="isLinearFeatureEnabled && isLinearClientIdConfigured"
            class="contact-details-panel__accordion contact-details-panel__accordion--linear"
            :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
            :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_linear_issues_open')"
          >
            <template #icon>
              <span
                class="i-lucide-git-branch contact-details-panel__accordion-icon"
              />
            </template>
            <LinearSetupCTA v-if="!isLinearConnected" />
            <LinearIssuesList v-else :conversation-id="conversationId" />
          </AccordionItem>

          <AccordionItem
            v-if="isShopifyFeatureEnabled"
            class="contact-details-panel__accordion contact-details-panel__accordion--shopify"
            :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
            :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
            compact
            show-chevron
            animated
            :draggable="false"
            @toggle="toggleSidebarUIState('is_shopify_orders_open')"
          >
            <template #icon>
              <span
                class="i-lucide-shopping-bag contact-details-panel__accordion-icon"
              />
            </template>
            <ShopifyOrdersList :contact-id="contactId" />
          </AccordionItem>
        </div>
      </div>
      <GroupMembers
        v-if="isWhatsappGroup"
        ref="groupMembersRef"
        :group="whatsappGroup"
        :inbox-id="inboxId"
        :access-token="currentUser?.access_token || ''"
        @group-updated="refreshWhatsappGroup"
      />
      <div
        class="contact-details-panel__footer-actions !mx-0 !gap-2 !px-4 !pb-[calc(1rem+env(safe-area-inset-bottom))] md:!mx-2 md:!gap-5 md:!px-[10px] md:!pb-7"
      >
        <button
          type="button"
          class="contact-details-panel__footer-action contact-details-panel__footer-action--danger contact-details-panel__footer-action--hidden"
          @click="openClearConversationModal"
        >
          <span
            class="i-lucide-circle-minus contact-details-panel__footer-action-icon"
          />
          <span>
            {{
              isWhatsappGroup
                ? groupClearConversationLabel
                : clearConversationLabel
            }}
          </span>
        </button>
        <button
          v-if="isWhatsappGroup"
          type="button"
          class="contact-details-panel__footer-action contact-details-panel__footer-action--danger"
          @click="openLeaveGroupModal"
        >
          <span
            class="i-lucide-log-out contact-details-panel__footer-action-icon"
          />
          <span>{{ groupLeaveLabel }}</span>
        </button>
        <button
          v-if="!isWhatsappGroup"
          type="button"
          class="contact-details-panel__footer-action contact-details-panel__footer-action--danger"
          @click="openBlockConversationModal"
        >
          <span
            class="i-lucide-ban contact-details-panel__footer-action-icon"
          />
          <span>{{ blockContactLabel }}</span>
        </button>
        <button
          v-if="!isWhatsappGroup || hasLeftWhatsappGroup"
          type="button"
          class="contact-details-panel__footer-action contact-details-panel__footer-action--danger contact-details-panel__footer-action--hidden"
          @click="openDeleteConversationModal"
        >
          <span
            class="i-lucide-trash-2 contact-details-panel__footer-action-icon"
          />
          <span>{{ deleteConversationLabel }}</span>
        </button>
        <p v-if="isWhatsappGroup" class="contact-details-panel__group-created">
          {{ groupCreatedByLabel }}
          <span class="contact-details-panel__group-created-value">
            <span v-if="groupCreatedByName">{{ groupCreatedByName }}</span>
            <span v-else>
              {{ groupCreatedByPhone || groupNumberUnavailableLabel }}
            </span>
          </span>
          <span aria-hidden="true">{{ groupCreatedSeparator }}</span>
          {{ groupCreatedAtLabel }}
        </p>
      </div>
      <Teleport to="body">
        <Transition name="contact-delete-modal">
          <div
            v-if="showClearConversationModal"
            class="contact-delete-modal-backdrop"
            tabindex="-1"
            @click.self="closeClearConversationModal"
            @keydown.esc.stop.prevent="closeClearConversationModal"
          >
            <div
              class="contact-delete-modal contact-delete-modal--clear"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="contact-clear-modal-title"
              aria-describedby="contact-clear-modal-description"
            >
              <div class="contact-delete-modal__content">
                <h2
                  id="contact-clear-modal-title"
                  class="contact-delete-modal__title"
                >
                  {{ clearConversationTitle }}
                </h2>
                <p
                  id="contact-clear-modal-description"
                  class="contact-delete-modal__description"
                >
                  {{ clearConversationDescription }}
                </p>
                <div class="contact-delete-modal__actions">
                  <button
                    type="button"
                    class="contact-delete-modal__cancel"
                    :disabled="isClearingConversation"
                    @click="closeClearConversationModal"
                  >
                    {{ cancelDeleteConversationLabel }}
                  </button>
                  <button
                    type="button"
                    class="contact-delete-modal__confirm"
                    :disabled="isClearingConversation"
                    @click="clearConversation"
                  >
                    {{
                      isClearingConversation
                        ? clearingConversationLabel
                        : confirmClearConversationLabel
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
        <Transition name="contact-delete-modal">
          <div
            v-if="showBlockConversationModal"
            class="contact-delete-modal-backdrop"
            tabindex="-1"
            @click.self="closeBlockConversationModal"
            @keydown.esc.stop.prevent="closeBlockConversationModal"
          >
            <div
              class="contact-delete-modal contact-delete-modal--block"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="contact-block-modal-title"
              aria-describedby="contact-block-modal-description"
              @click.stop
            >
              <div class="contact-delete-modal__content">
                <h2
                  id="contact-block-modal-title"
                  class="contact-delete-modal__title"
                >
                  {{ blockConversationTitle }}
                </h2>
                <p
                  id="contact-block-modal-description"
                  class="contact-delete-modal__description"
                >
                  {{ blockConversationDescription }}
                </p>
                <div class="contact-delete-modal__actions">
                  <button
                    type="button"
                    class="contact-delete-modal__cancel"
                    :disabled="isUpdatingConversationBlock"
                    @click="closeBlockConversationModal"
                  >
                    {{ cancelDeleteConversationLabel }}
                  </button>
                  <button
                    type="button"
                    class="contact-delete-modal__confirm"
                    :disabled="isUpdatingConversationBlock"
                    @click="toggleMuteConversation"
                  >
                    {{
                      isUpdatingConversationBlock
                        ? updatingBlockConversationLabel
                        : confirmBlockConversationLabel
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
        <Transition name="contact-delete-modal">
          <div
            v-if="showLeaveGroupModal"
            class="contact-delete-modal-backdrop"
            tabindex="-1"
            @click.self="closeLeaveGroupModal"
            @keydown.esc.stop.prevent="closeLeaveGroupModal"
          >
            <div
              class="contact-delete-modal contact-delete-modal--leave"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="contact-leave-group-modal-title"
              aria-describedby="contact-leave-group-modal-description"
            >
              <div class="contact-delete-modal__content">
                <h2
                  id="contact-leave-group-modal-title"
                  class="contact-delete-modal__title"
                >
                  {{ leaveGroupTitle }}
                </h2>
                <p
                  id="contact-leave-group-modal-description"
                  class="contact-delete-modal__description"
                >
                  {{ leaveGroupDescription }}
                </p>
                <div class="contact-delete-modal__actions">
                  <button
                    type="button"
                    class="contact-delete-modal__cancel"
                    :disabled="isLeavingWhatsappGroup"
                    @click="closeLeaveGroupModal"
                  >
                    {{ cancelDeleteConversationLabel }}
                  </button>
                  <button
                    type="button"
                    class="contact-delete-modal__confirm"
                    :disabled="isLeavingWhatsappGroup"
                    @click="leaveWhatsappGroup"
                  >
                    {{
                      isLeavingWhatsappGroup
                        ? leavingGroupLabel
                        : confirmLeaveGroupLabel
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
        <Transition name="contact-delete-modal">
          <div
            v-if="showDeleteConversationModal"
            class="contact-delete-modal-backdrop"
            tabindex="-1"
            @click.self="closeDeleteConversationModal"
            @keydown.esc.stop.prevent="closeDeleteConversationModal"
          >
            <div
              class="contact-delete-modal contact-delete-modal--delete"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="contact-delete-modal-title"
              aria-describedby="contact-delete-modal-description"
            >
              <div class="contact-delete-modal__content">
                <h2
                  id="contact-delete-modal-title"
                  class="contact-delete-modal__title"
                >
                  {{ deleteConversationTitle }}
                </h2>
                <p
                  id="contact-delete-modal-description"
                  class="contact-delete-modal__description"
                >
                  {{ deleteConversationDescription }}
                </p>
                <div class="contact-delete-modal__actions">
                  <button
                    type="button"
                    class="contact-delete-modal__cancel"
                    :disabled="isDeletingConversation"
                    @click="closeDeleteConversationModal"
                  >
                    {{ cancelDeleteConversationLabel }}
                  </button>
                  <button
                    type="button"
                    class="contact-delete-modal__confirm"
                    :disabled="isDeletingConversation"
                    @click="deleteConversation"
                  >
                    {{
                      isDeletingConversation
                        ? deletingConversationLabel
                        : confirmDeleteConversationLabel
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.contact-details-panel__header {
  position: sticky;
  top: 0;
  z-index: 20;
}

.contact-details-panel__header h2 {
  font-size: 1.25rem;
}

.contact-details-panel__header-button {
  width: 48px;
  min-width: 48px;
  height: 48px;
  min-height: 48px;
  border-radius: 9999px;
  color: rgb(var(--slate-12));
  transition:
    background 160ms ease,
    color 160ms ease;
}

.contact-details-panel__header-button:hover,
.contact-details-panel__header-button:focus-visible {
  border-radius: 9999px;
  background: rgba(194, 189, 184, 0.18);
  color: rgb(var(--slate-12));
  outline: none;
}

.contact-details-panel__header-button :deep([class*='i-']) {
  width: 24px;
  height: 24px;
  font-size: 24px;
}

.contact-details-panel__body {
  scrollbar-width: thin;
  scrollbar-color: rgba(148, 163, 184, 0.42) transparent;
}

.contact-details-panel__body::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.contact-details-panel__body::-webkit-scrollbar-track {
  background: transparent;
}

.contact-details-panel__body::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.42);
  border-radius: 999px;
}

:deep(.contact-details-panel__accordion) {
  width: 100%;
}

:deep(.contact-details-panel__accordion > button) {
  min-height: 62px;
  height: 62px;
  border: 0;
  background: transparent;
  outline: 0;
  cursor: pointer;
}

:deep(.contact-details-panel__accordion > button:hover),
:deep(.contact-details-panel__accordion > button:focus-visible) {
  background: rgba(194, 189, 184, 0.12);
}

:deep(.contact-details-panel__accordion > button > div:first-child) {
  align-items: center;
  gap: 14px;
}

:deep(.contact-details-panel__accordion > button .inline-block) {
  display: none;
}

.contact-details-panel__accordion-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.6);
  font-size: 24px;
}

:deep(.contact-details-panel__accordion > button h5) {
  display: inline-flex;
  align-items: center;
  color: rgb(var(--slate-12));
  font-size: 16px;
  font-weight: 500;
}

:deep(.contact-details-panel__accordion > button > div:last-child > div) {
  display: none;
}

:deep(.contact-details-panel__accordion > div) {
  border: 0;
  background: transparent;
  outline: 0;
}

.contact-details-panel__footer-actions {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 8px;
  padding: 12px 10px 28px;
  border-top: 0;
}

.contact-details-panel__footer-action {
  display: flex;
  width: 100%;
  min-height: 62px;
  height: 62px;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #202124;
  cursor: pointer;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.25;
  text-align: left;
  transition: background 160ms ease;
}

.contact-details-panel__footer-action:hover,
.contact-details-panel__footer-action:focus-visible {
  background: rgba(217, 45, 32, 0.08);
  outline: none;
}

.contact-details-panel__footer-action--danger {
  color: #d92d20;
}

.contact-details-panel__footer-action--hidden {
  display: none;
}

.contact-details-panel__group-created {
  margin: 4px 0 0;
  margin-left: 0;
  padding: 8px 16px 0 0;
  color: #7a7f86;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4;
  white-space: nowrap;
}

.contact-details-panel__group-created-value {
  color: inherit;
  font-weight: 400;
}

.contact-details-panel__footer-action-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: currentColor;
  font-size: 24px;
}

.contact-delete-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2147483648;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.55);
}

.contact-delete-modal {
  width: min(30rem, calc(100vw - 2rem));
  max-height: calc(100vh - 2rem);
  overflow: auto;
  border-radius: 2rem;
  background: #fff;
  padding: 2rem 2rem 1.5rem;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.24);
}

.contact-delete-modal--block {
  width: min(43rem, calc(100vw - 2rem));
}

.contact-delete-modal--clear,
.contact-delete-modal--leave,
.contact-delete-modal--delete {
  width: min(30rem, calc(100vw - 2rem));
}

.contact-delete-modal__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.contact-delete-modal__title {
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.25;
}

.contact-delete-modal__description {
  max-width: 30rem;
  margin: 1.5rem 0 0;
  color: #6b7280;
  font-size: 1.125rem;
  line-height: 1.5;
}

.contact-delete-modal__actions {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 1.5rem;
  padding-top: 3rem;
}

.contact-delete-modal__cancel,
.contact-delete-modal__confirm {
  display: inline-flex;
  min-width: 9.5rem;
  min-height: 3.5rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.75rem 1.75rem;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.25;
}

.contact-delete-modal__cancel {
  border: 0;
  background: transparent;
  color: #278c68;
  border-radius: 50px;
}

.contact-delete-modal__cancel:hover:not(:disabled) {
  background: #e7f5ed;
  color: #1f7053;
}

.contact-delete-modal__confirm {
  border: 0;
  background: #f0003c;
  color: #fff;
}

.contact-delete-modal__cancel:disabled,
.contact-delete-modal__confirm:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.contact-delete-modal-enter-active,
.contact-delete-modal-leave-active {
  transition: opacity 0.2s ease;
}

.contact-delete-modal-enter-from,
.contact-delete-modal-leave-to {
  opacity: 0;
}

@media (max-width: 767px) {
  .contact-details-panel {
    min-height: 0;
    max-width: 100%;
  }

  .contact-details-panel__body {
    min-width: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .contact-details-panel__group-created {
    white-space: normal;
    overflow-wrap: anywhere;
  }
}

@media (max-width: 640px) {
  .contact-delete-modal {
    width: min(30rem, calc(100vw - 1rem));
    padding: 2rem 1.25rem 1.5rem;
  }

  .contact-delete-modal__actions {
    flex-direction: column-reverse;
  }

  .contact-delete-modal__cancel,
  .contact-delete-modal__confirm {
    width: 100%;
  }
}

:deep(.contact--profile) {
  @apply pb-3 border-b border-solid border-n-weak;
}
</style>
